// Reusable editor for mapping page inputs (or static values) to a target
// object's parameters. Used by the embed block (D/V criteria, T add/edit
// pre-fills) and the navigate button click (D/V/T landing args).
//
// Each row is an ArgMapping: { target, label?, source }. The target
// dropdown lists `availableTargets` — keys exposed by the destination
// object (e.g. transaction columns, dashboard GLOBAL_CRITERIA keys, visual
// CRITERIA fields). Free-text fallback is supported when the destination
// metadata is unknown.

import { Plus, Trash2 } from 'lucide-react'
import { makeArgMapping } from './blocks'
import type { ArgMapping, FilterValueRef, PageInput } from './types'
import { useProjectVariables, type ProjectVarOption } from './useProjectVariables'

export interface ArgTargetOption {
  name: string                   // canonical column / criterion key
  label?: string                 // friendly label (heading / description)
  hint?: string                  // optional secondary info (data type, etc.)
}

interface Props {
  args: ArgMapping[] | undefined
  inputs: PageInput[]
  availableTargets: ArgTargetOption[]   // destination's known parameter keys
  metaUnavailableHint?: string          // shown when availableTargets is empty
  emptyHint?: string                    // shown when args is empty
  title?: string                        // section heading
  project?: string                      // enables Project-variable options
  onChange: (next: ArgMapping[]) => void
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 0,
  display: 'block',
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

export default function ArgMappingEditor({
  args,
  inputs,
  availableTargets,
  metaUnavailableHint,
  emptyHint,
  title = 'Arguments',
  project,
  onChange,
}: Props) {
  const list = args || []
  const projectVars = useProjectVariables(project)

  const update = (id: string, patch: Partial<ArgMapping>) => {
    onChange(list.map((a) => (a.id === id ? { ...a, ...patch } : a)))
  }
  const remove = (id: string) => onChange(list.filter((a) => a.id !== id))
  const add = () => onChange([...list, makeArgMapping()])

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <label style={FIELD_LABEL}>{title}</label>
        <button onClick={(e) => { e.stopPropagation(); add() }} className="btn btn-ghost btn-sm">
          <Plus size={13} /> Add
        </button>
      </div>

      {list.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          {emptyHint || 'No arguments. Add a row to pass a page input or a static value to the destination.'}
        </div>
      )}

      {availableTargets.length === 0 && metaUnavailableHint && list.length > 0 && (
        <div style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          marginBottom: 6, fontStyle: 'italic',
        }}>{metaUnavailableHint}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((a) => (
          <ArgRow
            key={a.id}
            arg={a}
            inputs={inputs}
            projectVars={projectVars}
            availableTargets={availableTargets}
            onChange={(patch) => update(a.id, patch)}
            onRemove={() => remove(a.id)}
          />
        ))}
      </div>
    </div>
  )
}

function ArgRow({
  arg,
  inputs,
  projectVars,
  availableTargets,
  onChange,
  onRemove,
}: {
  arg: ArgMapping
  inputs: PageInput[]
  projectVars: ProjectVarOption[]
  availableTargets: ArgTargetOption[]
  onChange: (patch: Partial<ArgMapping>) => void
  onRemove: () => void
}) {
  // If the current target isn't in the available list, fall back to a free-text
  // input — keeps stale mappings editable instead of silently dropping them.
  const knownTarget = availableTargets.some((t) => t.name === arg.target)
  const useFreeText = availableTargets.length === 0 || (!!arg.target && !knownTarget)

  return (
    <div style={{
      padding: 8,
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Target column / parameter
          </label>
          {useFreeText ? (
            <input
              value={arg.target}
              onChange={(e) => onChange({ target: e.target.value })}
              placeholder="e.g. cust_code"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          ) : (
            <select
              value={arg.target}
              onChange={(e) => onChange({ target: e.target.value })}
              style={INPUT}
            >
              <option value="">— Select target —</option>
              {arg.target && !availableTargets.some((t) => t.name === arg.target) && (
                <option value={arg.target}>{arg.target} (not in the destination&apos;s parameters)</option>
              )}
              {availableTargets.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.label ? `${t.label} (${t.name})` : t.name}
                  {t.hint ? ` · ${t.hint}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="btn btn-ghost btn-sm"
          style={{ marginTop: 18, padding: 4, color: 'var(--color-error)' }}
          title="Remove"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          Friendly label (optional)
        </label>
        <input
          value={arg.label || ''}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g. Selected customer"
          style={INPUT}
        />
      </div>

      <SourceEditor
        inputs={inputs}
        projectVars={projectVars}
        value={arg.source}
        onChange={(source) => onChange({ source })}
      />
    </div>
  )
}

function SourceEditor({
  inputs,
  projectVars,
  value,
  onChange,
}: {
  inputs: PageInput[]
  projectVars: ProjectVarOption[]
  value: FilterValueRef | undefined
  onChange: (v: FilterValueRef) => void
}) {
  const v = value || { source: 'page_input', name: '' }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Source</label>
      <div style={{ display: 'flex', gap: 4 }}>
        <select
          value={v.source}
          onChange={(e) => {
            const source = e.target.value as 'page_input' | 'static'
            onChange({
              source,
              name: source === 'page_input' ? '' : undefined,
              value: source === 'static' ? '' : undefined,
            })
          }}
          style={{ ...INPUT, width: 110 }}
        >
          <option value="page_input">Page input</option>
          <option value="static">Static</option>
        </select>
        {v.source === 'page_input' ? (
          <select
            value={v.name || ''}
            onChange={(e) => onChange({ source: 'page_input', name: e.target.value })}
            style={{ ...INPUT, flex: 1 }}
          >
            <option value="">— Pick input —</option>
            {/* Keep a saved name the page doesn't declare visible — blanking
                it hid the binding and the next edit discarded it. */}
            {v.name && !inputs.some((i) => i.name === v.name) && !projectVars.some((pv) => pv.value === v.name) && (
              <option value={v.name}>{v.name} (not declared on this page)</option>
            )}
            {inputs.length > 0 && (
              <optgroup label="Page inputs">
                {inputs.map((inp) => (
                  <option key={inp.name} value={inp.name}>{inp.label || inp.name}</option>
                ))}
              </optgroup>
            )}
            {projectVars.length > 0 && (
              <optgroup label="Project variables">
                {projectVars.map((pv) => (
                  <option key={pv.value} value={pv.value}>{pv.label}</option>
                ))}
              </optgroup>
            )}
          </select>
        ) : (
          <input
            value={v.value || ''}
            onChange={(e) => onChange({ source: 'static', value: e.target.value })}
            placeholder="Static value (supports {{vars}})"
            style={{ ...INPUT, flex: 1 }}
          />
        )}
      </div>
    </div>
  )
}
